import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, XCircle, AlertTriangle, CreditCard } from 'lucide-react';
import { cancelTransaction } from '@/lib/api';
import { toast } from 'sonner';
import { fmt2 } from '@/lib/utils';

export const CancelDialog = ({ open, onOpenChange, txn, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!txn) return null;
  const paymentStatus = txn.payment_status || 'unpaid';
  const willRefund = paymentStatus === 'paid';
  const total = txn.payment_total_EUR ?? (txn.price_EUR || 0);

  const handleSubmit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      toast.error('Bitte geben Sie einen Grund an (mind. 3 Zeichen)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cancelTransaction(txn.transaction_id, trimmed);
      if (res.data?.refund) {
        toast.success(`Storniert · Erstattung von ${fmt2(res.data.refund.amount_eur)} EUR initiiert`);
      } else {
        toast.success('Bestellung storniert');
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stornierung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="cancel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            Bestellung stornieren
          </DialogTitle>
          <DialogDescription>
            {willRefund
              ? 'Die bereits geleistete Zahlung wird automatisch über Stripe erstattet.'
              : 'Diese Bestellung wurde noch nicht bezahlt und kann direkt storniert werden.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
            <p className="font-medium">{txn.material?.common_title || 'Artikel'}</p>
            <p className="text-muted-foreground text-xs">
              Bestell-Nr.: {txn.order_number || txn.transaction_id?.slice(0, 8)} · Menge: {txn.qty}
            </p>
            {total > 0 && (
              <p className="text-muted-foreground text-xs">
                Gesamtbetrag: <span className="font-medium text-foreground">{fmt2(total)} EUR</span>
              </p>
            )}
          </div>

          {willRefund && (
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-300">Erstattung über Stripe</p>
                <p className="text-amber-800 dark:text-amber-400">
                  Der ursprüngliche Zahlbetrag wird vollständig auf das ursprüngliche Zahlungsmittel zurückgebucht (1–5 Werktage).
                </p>
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm">Grund für die Stornierung *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="z.B. Mengenfehler, doppelte Bestellung, nicht mehr benötigt …"
              className="mt-1"
              data-testid="cancel-reason-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            className="bg-red-500 hover:bg-red-600"
            onClick={handleSubmit}
            disabled={submitting || reason.trim().length < 3}
            data-testid="cancel-confirm-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : willRefund ? (
              <CreditCard className="h-4 w-4 mr-1" />
            ) : (
              <XCircle className="h-4 w-4 mr-1" />
            )}
            {willRefund ? 'Stornieren & Erstatten' : 'Stornieren'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
